import React, { useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  CssBaseline,
  Paper,
  Chip,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ReplayIcon from "@mui/icons-material/Replay";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Container from "@mui/material/Container";
import { useTheme } from "@mui/material/styles";
import FlyLogo from "../assets/images/flyhi_logo_full.svg";
import signUPImage from "../assets/images/signUP.svg";
import CStepper from "../components/utils/CStepper";
import CMenu from "../components/utils/CMenu";
import MobileViewHeader from "../components/header/MobileViewHeader";



const MismatchIllustration = () => (
  <Box sx={{ position: "relative", width: 180, height: 160, mx: "auto", mb: 3 }}>
    <svg
      viewBox="0 0 180 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      width="180"
      height="160"
    >
      {/* Aadhaar Card Side */}
      <rect x="6" y="28" width="64" height="80" rx="10" fill="#EBF3FD" stroke="#90C2F5" strokeWidth="1.5" />
      {/* Card lines */}
      <rect x="18" y="72" width="40" height="4" rx="2" fill="#B5D4F4" />
      <rect x="18" y="82" width="28" height="3" rx="1.5" fill="#CDDFF7" />
      <rect x="18" y="91" width="32" height="3" rx="1.5" fill="#CDDFF7" />
      {/* Person avatar on card */}
      <circle cx="38" cy="52" r="13" fill="#B5D4F4" />
      <circle cx="38" cy="46" r="5" fill="#85B7EB" />
      <path d="M26 64 Q26 58 38 58 Q50 58 50 64" fill="#85B7EB" />
      {/* Card label */}
      <text x="38" y="23" fontSize="9" fill="#185FA5" textAnchor="middle" fontWeight="600">
        AADHAAR
      </text>

      {/* Selfie / Camera Side */}
      <rect x="110" y="28" width="64" height="80" rx="10" fill="#FFF0EC" stroke="#F5B9A8" strokeWidth="1.5" />
      {/* Camera frame corners */}
      <path d="M118 36 L118 32 L122 32" stroke="#D85A30" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M166 36 L166 32 L162 32" stroke="#D85A30" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M118 100 L118 104 L122 104" stroke="#D85A30" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M166 100 L166 104 L162 104" stroke="#D85A30" strokeWidth="1.5" strokeLinecap="round" />
      {/* Person avatar on selfie */}
      <circle cx="142" cy="52" r="13" fill="#F5C4B3" />
      <circle cx="142" cy="46" r="5" fill="#F0997B" />
      <path d="M130 64 Q130 58 142 58 Q154 58 154 64" fill="#F0997B" />
      {/* Selfie label */}
      <text x="142" y="23" fontSize="9" fill="#993C1D" textAnchor="middle" fontWeight="600">
        SELFIE
      </text>
      {/* Wavy lines below avatar to differentiate the face */}
      <path d="M134 72 Q138 69 142 72 Q146 75 150 72" stroke="#F0997B" strokeWidth="1.2" fill="none" />

      {/* Dashed connector line */}
      <line x1="70" y1="68" x2="110" y2="68" stroke="#E24B4A" strokeWidth="1.5" strokeDasharray="4 3" />

      {/* Cross badge in center */}
      <circle cx="90" cy="68" r="14" fill="#FCEBEB" stroke="#F09595" strokeWidth="1.5" />
      <line x1="84" y1="62" x2="96" y2="74" stroke="#A32D2D" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="96" y1="62" x2="84" y2="74" stroke="#A32D2D" strokeWidth="2.5" strokeLinecap="round" />

      {/* Exclamation dot bottom */}
      <circle cx="90" cy="130" r="4" fill="#E24B4A" />
      <rect x="87.5" y="112" width="5" height="12" rx="2.5" fill="#E24B4A" />
    </svg>
  </Box>
);

const AadhaarAndUserSelfieMismatch = () => {
  const handleCallSupport = () => {
    window.location.href = "tel:8655873308";
  };
const stepperCount = 5;

    const matches = useMediaQuery((theme) => theme.breakpoints.up("sm"));
    const theme = useTheme();

  function renderMainBody() {
    // wire to your chat support flow
      return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#F7F8FA",
        px: 2,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 420, marginBottom: 16 }}>
        {/* Status chip */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Chip
            label="VERIFICATION FAILED"
            size="small"
            sx={{
              bgcolor: "#FCEBEB",
              color: "#A32D2D",
              fontWeight: 600,
              fontSize: 11,
              letterSpacing: "0.06em",
              border: "1px solid #F09595",
              borderRadius: "6px",
              height: 26,
            }}
          />
        </Box>

        {/* Illustration */}
        <MismatchIllustration />

        {/* Heading */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: 20,
            textAlign: "center",
            color: "text.primary",
            mb: 1,
          }}
        >
          Aadhar Photo Mismatch With Selfie
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            textAlign: "center",
            color: "text.secondary",
            fontSize: 14,
            lineHeight: 1.75,
            mb: 3,
            px: 1,
          }}
        >
          Unfortunately, we couldn’t do a successful match between your selfie and aadhar photo. 
          Kindly contact customer care.
        </Typography>

        {/* Customer care card */}
        <Paper
          elevation={0}
          onClick={handleCallSupport}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 3,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "&:hover": { bgcolor: "#F3F4F6" },
            transition: "background 0.15s",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                bgcolor: "#EAF3DE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PhoneIcon sx={{ fontSize: 18, color: "#3B6D11" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1 }}>
                Need help? Contact us
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#185FA5", mt: 0.3 }}>
                86558 73308
              </Typography>
            </Box>
          </Box>
          <ChevronRightIcon sx={{ color: "text.disabled" }} />
        </Paper>

      </Box>
    </Box>
  );
  }


    return (
      <div style={{ height: "auto" }}>
        {/* <CAlert /> */}
        <AppBar
          sx={{ backgroundColor: theme.palette.headerColor }}
          position="static"
        >
          {matches ? (
            <Container maxWidth="xl">
              <Toolbar
                sx={{
                  display: "grid",
                  gridAutoFlow: "column",
                  justifyContent: "space-between",
                }}
                disableGutters
              >
                <img
                  style={{
                    marginTop: "10px",
                    marginBottom: "10px",
                    width: "50px",
                    height: "50px",
                  }}
                  src={FlyLogo}
                  alt="logo"
                />
                <div
                  sx={{
                    pt: 6,
                    gridColumn: "3 / 5",
                    display: { xs: "none", md: "block", lg: "none" },
                  }}
                >
                  <CStepper tabValue={stepperCount} />
                </div>
                <div
                  onClick={() => {}}
                  sx={{
                    gridColumn: "4 / 5",
                    textAlign: "left",
                    fontFamily: "lato",
                    cursor: "pointer",
                  }}
                  variant="body1"
                >
                  <CMenu />
                </div>
              </Toolbar>
            </Container>
          ) : (
            <MobileViewHeader
              stepperCount={stepperCount + 1}
              header="Loan Agreement"
              nextHeader="Loan Agreement"
            />
          )}
        </AppBar>
        <Grid container component="main" sx={{ height: "100vh" }}>
          <CssBaseline />
  
          <Grid item xs={12} sm={6} md={6} component={Paper}>
             {renderMainBody()}
          </Grid>
          <Grid
            item
            display={{ xs: "none", lg: "block", sm: "block" }}
            sm={6}
            md={6}
            sx={{
              backgroundRepeat: "no-repeat",
              backgroundColor: (t) =>
                t.palette.mode === "light"
                  ? t.palette.grey[50]
                  : t.palette.grey[900],
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <img src={signUPImage} alt="signup" />
          </Grid>
        </Grid>
      </div>
    );
};

export default AadhaarAndUserSelfieMismatch;